import React, { useState, useEffect } from 'react';
import { Outlet, useParams, Link } from 'react-router-dom';
import { Bars3Icon, ChevronLeftIcon, ChevronRightIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import Sidebar from './Sidebar';
import Breadcrumbs from './Breadcrumbs';
import PlaybookTOC from '../playbooks/PlaybookTOC';
import { getPlaybookById, getSectionById, getNavigationLinks } from '../../data/playbookTaxonomy';
import useScrollReset from '../../hooks/useScrollReset';

export default function PlaybookLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const { playbookId, sectionId } = useParams();
  
  // FORCE SCROLL RESET ON PLAYBOOK LAYOUT LEVEL
  useScrollReset([playbookId, sectionId]);
  
  const playbook = getPlaybookById(playbookId);
  const section = getSectionById(playbookId, sectionId);
  const { prev, next } = getNavigationLinks(playbookId, sectionId);

  // Generate breadcrumbs
  const breadcrumbs = [
    { label: 'Home', to: '/dashboard' },
    { label: 'Playbook Library', to: '/content' },
    ...(playbook ? [{ label: playbook.title, to: `/playbooks/${playbookId}/intro` }] : []),
    ...(section ? [{ label: section.title }] : [])
  ].filter(Boolean);

  // Sidebar width for collapsed/expanded (desktop only)
  const sidebarWidth = sidebarExpanded ? 'w-64' : 'w-16';

  // Get all headings for mobile TOC
  const [headings, setHeadings] = useState([]);

  // Scan the page for headings (for mobile TOC)
  useEffect(() => {
    if (!mobileTocOpen) return;
    
    const getHeadings = () => {
      // Find all h1, h2, h3 elements in the main content area
      const headingElements = Array.from(document.querySelectorAll('main h1, main h2, main h3'));
      
      const headingData = headingElements.map((heading, index) => {
        // Create an id if the heading doesn't have one
        if (!heading.id) {
          const id = `heading-${index}`;
          heading.id = id;
        }
        
        return {
          id: heading.id,
          text: heading.textContent,
          level: Number(heading.tagName.substring(1)) // Extract number from h1, h2, h3
        };
      });
      
      setHeadings(headingData);
    };
    
    // Allow time for the DOM to update
    const timer = setTimeout(() => {
      getHeadings();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [mobileTocOpen, sectionId]);

  const handleHeadingClick = (e, id) => {
    e.preventDefault();
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'auto', block: 'start' });
      setMobileTocOpen(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Sidebar (desktop) */}
      <aside
        className={`
          hidden lg:block bg-gray-900 border-r border-gray-800 transition-all duration-200 ease-in-out
          ${sidebarWidth} h-screen fixed top-0 left-0 z-30
        `}
      >
        <div className="h-full overflow-y-auto">
          <Sidebar closeSidebar={() => setSidebarOpen(false)} minimal={!sidebarExpanded} inPlaybookLayout={true} />
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div
        className={`
          fixed lg:hidden top-0 left-0 z-40 h-full w-64 bg-gray-900 border-r border-gray-800 transform
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          transition-transform duration-200 ease-in-out
        `}
      >
        <Sidebar closeSidebar={() => setSidebarOpen(false)} minimal={false} inPlaybookLayout={true} />
      </div>

      {/* Mobile TOC Drawer */}
      {mobileTocOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50" 
            onClick={() => setMobileTocOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-gray-900 border-l border-gray-700 overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-800">
              <h3 className="text-white font-medium">On this page</h3>
              <button
                onClick={() => setMobileTocOpen(false)}
                className="text-gray-400 hover:text-white p-1.5 rounded-md"
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
              </button>
            </div>
            <nav className="p-4">
              {headings.map((heading) => (
                <a
                  key={heading.id}
                  href={`#${heading.id}`}
                  className={`block py-2 px-3 rounded mb-1 
                    ${heading.level > 1 ? 'ml-3' : ''}
                    text-gray-400 hover:text-white hover:bg-gray-800/50
                    truncate
                  `}
                  onClick={(e) => handleHeadingClick(e, heading.id)}
                  title={heading.text}
                >
                  {heading.text}
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className={`flex-1 min-h-screen overflow-x-hidden ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}`}>
        {/* Header with Hamburger + Breadcrumbs */}
        <header className="sticky top-0 z-20 bg-gray-900 border-b border-gray-800">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center">
              <button
                className="text-gray-400 hover:text-white mr-3 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              {/* Collapse/Expand button for desktop */}
           
              <Breadcrumbs items={breadcrumbs} />
            </div>
            
            {/* Mobile TOC button */}
            <button
              className="text-gray-400 hover:text-white p-1.5 rounded-md lg:hidden"
              onClick={() => setMobileTocOpen(true)}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="relative">
          {/* TOC - right side fixed (renders itself based on headings) */}
          <PlaybookTOC />
          
          {/* Main Content Area */}
          <div className="px-4 sm:px-8 lg:px-12 py-8 max-w-screen-2xl mx-auto">
            <main className="max-w-full md:max-w-2xl lg:max-w-3xl xl:max-w-5xl 2xl:max-w-6xl lg:pr-52">
              <div className="mx-auto mb-24">
                <Outlet key={`${playbookId}-${sectionId}`} />
              </div>

              {/* Navigation Footer */}
              {(prev || next) && (
                <div className="mt-16 pt-8 border-t border-gray-800">
                  <div className="grid grid-cols-2 gap-4">
                    {prev && (
                      <Link
                        to={`/playbooks/${playbookId}/${prev.id}`}
                        className="group flex items-center p-4 rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
                      >
                        <ChevronLeftIcon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                        <div className="ml-4">
                          <div className="text-sm text-gray-400 group-hover:text-white transition-colors">Previous</div>
                          <div className="text-base font-medium text-white truncate max-w-[200px]" title={prev.title}>{prev.title}</div>
                        </div>
                      </Link>
                    )}
                    {next && (
                      <Link
                        to={`/playbooks/${playbookId}/${next.id}`}
                        className="group flex items-center justify-end p-4 rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
                      >
                        <div className="mr-4 text-right">
                          <div className="text-sm text-gray-400 group-hover:text-white transition-colors">Next</div>
                          <div className="text-base font-medium text-white truncate max-w-[200px]" title={next.title}>{next.title}</div>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
} 